import React, {useMemo} from "react";
import {css, Theme} from "@emotion/react";

import {mb, ph, pr, w50} from "@pg-design/helpers-css";
import {
    AdditionalAreaIcon,
    AirportIcon,
    AreaIcon,
    BeachIcon,
    BookmarkIcon,
    CalendarCheckIcon,
    FloorsIcon,
    FloorUpIcon,
    KitchenIcon,
    RoomHeightIcon,
    RoomsIcon,
    WorldSideIcon
} from "@pg-design/icons-module";
import {identity, isEmpty} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";

import {Pictogram} from "../../atoms/Pictogram";
import {distanceFromFormat} from "../../offer/atoms/OfferUVP";
import {OfferType} from "../../offer/types/OfferType";
import {IRangeQuery} from "../../request/IRangeQuery";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {useConstructionEndDate} from "../hooks/useConstructionEndDate";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";

type ValueType = string | number | undefined | null;

interface IPropsPropertyDetail {
    label: string;
    mobileLabel?: string;
    value: ValueType;
    icon: React.ElementType;
}

const getPropertyDetail = (label: string, value: ValueType, icon: React.ElementType, mobileLabel?: string): IPropsPropertyDetail => {
    return {label, mobileLabel, value, icon};
};

const roomsPlural = pluralize(["pokój", "pokoje", "pokoi"]);
const floorsPlural = pluralize(["poziom", "poziomy", "poziomy"]);

export interface IPropertyDetailPictograms {
    property: {
        area: number;
        additional_areas: {
            type: number;
            area: number;
        }[];
        construction_end_date: string;
        floor: number;
        floors: number;
        has_attic: boolean;
        has_entresol: boolean;
        height_range: {
            lower: number;
            upper: number;
        };
        number: string;
        kitchen_type: number;
        offer: {
            distance_from_the_airport?: number | null;
            distance_from_the_beach?: number | null;
            construction_date_range: IRangeQuery<string>;
            type: number;
        };
        price: number | null;
        rooms: number;
        quarters: number[];
    };
    variables: {
        property_room_area_type: IValueLabel[];
        property_additional_areas: IValueLabel[];
        property_kitchen_type: IValueLabel[];
        property_quarters: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        property_ownership_form: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    className?: string;
}

export const PropertyDetailPictograms = (props: IPropertyDetailPictograms) => {
    const {property, variables} = props;

    const additionalAreaList = property.additional_areas.reduce((acc: IAdditionalArea[], aA: IAdditionalArea): IAdditionalArea[] => {
        const additionalAreaTypes = variables && variables.property_additional_areas;
        const foundAdditionalAreas = additionalAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundAdditionalAreas
            ? ([
                  ...acc,
                  {
                      area: aA.area,
                      type: foundAdditionalAreas.label
                  }
              ] as IAdditionalArea[])
            : acc;
    }, []);
    const additionalAreaListElement = () => additionalAreaList.map((item) => item.type.toString()).join(", ");

    const kitchenTypes = variables && variables.property_kitchen_type;
    const foundKitchenType = kitchenTypes?.find((kT) => kT.value === property.kitchen_type);
    const kitchenType: string = (foundKitchenType && foundKitchenType.label) || "";

    const quarterTypes = variables && variables.property_quarters;
    const foundQuarterTypes = property.quarters.map((q) => quarterTypes?.find((qT) => qT.value === q));
    const quarterTypeList = foundQuarterTypes.map((item) => (item && item.label.toLowerCase()) || "").filter(identity) as string[];
    const listQuarterTypesElements = quarterTypeList.map((qT) => `${qT}`);

    const {calculateConstructionEndDate} = useConstructionEndDate();

    const propertyDetailsData = useMemo(() => {
        if (!property) {
            return [];
        }

        return [
            getPropertyDetail(
                "Termin oddania",
                calculateConstructionEndDate(property.construction_end_date, property.offer.construction_date_range),
                CalendarCheckIcon,
                "Oddanie"
            ),
            getPropertyDetail("Ilość pokoi", `${property.rooms} ${roomsPlural(property.rooms)}`, RoomsIcon),
            getPropertyDetail("Metraż", property.area !== null ? areaFormat(property.area, {precision: 2}) : null, AreaIcon),
            getPropertyDetail("Piętro", property.floor === 0 ? "Parter" : property.floor ? `${property.floor} piętro` : null, FloorUpIcon),
            getPropertyDetail("Strona świata", !isEmpty(property.quarters) ? listQuarterTypesElements.join(", ") : null, WorldSideIcon),
            getPropertyDetail("Powierzchnie dodatkowe", !isEmpty(additionalAreaList) ? additionalAreaListElement() : null, AdditionalAreaIcon, "Dodatkowe"),
            getPropertyDetail("Kuchnia", kitchenType ? `${kitchenType.toLowerCase()} kuchnia` : null, KitchenIcon),
            getPropertyDetail(
                "Wysokość pomieszczeń",
                property.height_range.lower === property.height_range.upper
                    ? `${property.height_range.lower} cm wysokości`
                    : `od ${property.height_range.lower} do ${property.height_range.upper} cm wysokości`,
                RoomHeightIcon,
                "Wysokość"
            ),
            getPropertyDetail(
                `Ilość ${property.offer.type === OfferType.HOUSE ? "kondygnacji" : "poziomów"}`,
                `${property.floors} ${floorsPlural(property.floors)}${property.has_attic ? " w tym poddasze" : ""}${property.has_entresol ? " + antresola" : ""}`,
                FloorsIcon,
                `${property.offer.type === OfferType.HOUSE ? "Kondygnacje" : "Poziomy"}`
            ),
            getPropertyDetail("Oznaczenie oferty", property.number, BookmarkIcon),
            getPropertyDetail(
                "Odległość od lotniska",
                property.offer.distance_from_the_airport ? distanceFromFormat(property.offer.distance_from_the_airport) : null,
                AirportIcon
            ),
            getPropertyDetail(
                "Odległość od morza",
                property.offer.distance_from_the_beach ? distanceFromFormat(property.offer.distance_from_the_beach) : null,
                BeachIcon
            )
        ];
    }, [property]);

    const propertyRenderedPictograms: React.ReactNode[] = useMemo(() => {
        return propertyDetailsData.reduce<React.ReactNode[]>((acc, {label, mobileLabel, value, icon}) => {
            if (value !== null && typeof value !== "undefined") {
                acc.push(<Pictogram key={label} label={label} mobileLabel={mobileLabel} value={value as string | number} icon={icon} />);
            }
            return acc;
        }, []);
    }, [propertyDetailsData]);

    return (
        <div css={pictogramsWrapper} className={props.className}>
            {propertyRenderedPictograms}
        </div>
    );
};

const pictogramsWrapper = (theme: Theme) => css`
    display: flex;
    row-gap: 2.4rem;
    ${ph(2)};
    flex-wrap: wrap;
    align-items: flex-start;
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        row-gap: 4rem;
    }

    > div {
        ${w50};
        ${pr(2)};

        @media (min-width: ${theme.breakpoints.md}) {
            ${pr(0)};
        }
    }
`;
import React, {useMemo} from "react";
import {css, Theme} from "@emotion/react";

import {mb, ph, pr, w50} from "@pg-design/helpers-css";
import {
    AdditionalAreaIcon,
    AirportIcon,
    AreaIcon,
    BeachIcon,
    BookmarkIcon,
    CalendarCheckIcon,
    FloorsIcon,
    FloorUpIcon,
    KitchenIcon,
    RoomHeightIcon,
    RoomsIcon,
    WorldSideIcon
} from "@pg-design/icons-module";
import {identity, isEmpty} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";

import {Pictogram} from "../../atoms/Pictogram";
import {distanceFromFormat} from "../../offer/atoms/OfferUVP";
import {OfferType} from "../../offer/types/OfferType";
import {IRangeQuery} from "../../request/IRangeQuery";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {useConstructionEndDate} from "../hooks/useConstructionEndDate";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";

type ValueType = string | number | undefined | null;

interface IPropsPropertyDetail {
    label: string;
    mobileLabel?: string;
    value: ValueType;
    icon: React.ElementType;
}

const getPropertyDetail = (label: string, value: ValueType, icon: React.ElementType, mobileLabel?: string): IPropsPropertyDetail => {
    return {label, mobileLabel, value, icon};
};

const roomsPlural = pluralize(["pokój", "pokoje", "pokoi"]);
const floorsPlural = pluralize(["poziom", "poziomy", "poziomy"]);

export interface IPropertyDetailPictograms {
    property: {
        area: number;
        additional_areas: {
            type: number;
            area: number;
        }[];
        construction_end_date: string;
        floor: number;
        floors: number;
        has_attic: boolean;
        has_entresol: boolean;
        height_range: {
            lower: number;
            upper: number;
        };
        number: string;
        kitchen_type: number;
        offer: {
            distance_from_the_airport?: number | null;
            distance_from_the_beach?: number | null;
            construction_date_range: IRangeQuery<string>;
            type: number;
        };
        price: number | null;
        rooms: number;
        quarters: number[];
    };
    variables: {
        property_room_area_type: IValueLabel[];
        property_additional_areas: IValueLabel[];
        property_kitchen_type: IValueLabel[];
        property_quarters: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        property_ownership_form: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    className?: string;
}

export const PropertyDetailPictograms = (props: IPropertyDetailPictograms) => {
    const {property, variables} = props;

    const additionalAreaList = property.additional_areas.reduce((acc: IAdditionalArea[], aA: IAdditionalArea): IAdditionalArea[] => {
        const additionalAreaTypes = variables && variables.property_additional_areas;
        const foundAdditionalAreas = additionalAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundAdditionalAreas
            ? ([
                  ...acc,
                  {
                      area: aA.area,
                      type: foundAdditionalAreas.label
                  }
              ] as IAdditionalArea[])
            : acc;
    }, []);
    const additionalAreaListElement = () => additionalAreaList.map((item) => item.type.toString()).join(", ");

    const kitchenTypes = variables && variables.property_kitchen_type;
    const foundKitchenType = kitchenTypes?.find((kT) => kT.value === property.kitchen_type);
    const kitchenType: string = (foundKitchenType && foundKitchenType.label) || "";

    const quarterTypes = variables && variables.property_quarters;
    const foundQuarterTypes = property.quarters.map((q) => quarterTypes?.find((qT) => qT.value === q));
    const quarterTypeList = foundQuarterTypes.map((item) => (item && item.label.toLowerCase()) || "").filter(identity) as string[];
    const listQuarterTypesElements = quarterTypeList.map((qT) => `${qT}`);

    const {calculateConstructionEndDate} = useConstructionEndDate();

    const propertyDetailsData = useMemo(() => {
        if (!property) {
            return [];
        }

        return [
            getPropertyDetail(
                "Termin oddania",
                calculateConstructionEndDate(property.construction_end_date, property.offer.construction_date_range),
                CalendarCheckIcon,
                "Oddanie"
            ),
            getPropertyDetail("Ilość pokoi", `${property.rooms} ${roomsPlural(property.rooms)}`, RoomsIcon),
            getPropertyDetail("Metraż", property.area !== null ? areaFormat(property.area, {precision: 2}) : null, AreaIcon),
            getPropertyDetail("Piętro", property.floor === 0 ? "Parter" : property.floor ? `${property.floor} piętro` : null, FloorUpIcon),
            getPropertyDetail("Strona świata", !isEmpty(property.quarters) ? listQuarterTypesElements.join(", ") : null, WorldSideIcon),
            getPropertyDetail("Powierzchnie dodatkowe", !isEmpty(additionalAreaList) ? additionalAreaListElement() : null, AdditionalAreaIcon, "Dodatkowe"),
            getPropertyDetail("Kuchnia", kitchenType ? `${kitchenType.toLowerCase()} kuchnia` : null, KitchenIcon),
            getPropertyDetail(
                "Wysokość pomieszczeń",
                property.height_range.lower === property.height_range.upper
                    ? `${property.height_range.lower} cm wysokości`
                    : `od ${property.height_range.lower} do ${property.height_range.upper} cm wysokości`,
                RoomHeightIcon,
                "Wysokość"
            ),
            getPropertyDetail(
                `Ilość ${property.offer.type === OfferType.HOUSE ? "kondygnacji" : "poziomów"}`,
                `${property.floors} ${floorsPlural(property.floors)}${property.has_attic ? " w tym poddasze" : ""}${property.has_entresol ? " + antresola" : ""}`,
                FloorsIcon,
                `${property.offer.type === OfferType.HOUSE ? "Kondygnacje" : "Poziomy"}`
            ),
            getPropertyDetail("Oznaczenie oferty", property.number, BookmarkIcon),
            getPropertyDetail(
                "Odległość od lotniska",
                property.offer.distance_from_the_airport ? distanceFromFormat(property.offer.distance_from_the_airport) : null,
                AirportIcon
            ),
            getPropertyDetail(
                "Odległość od morza",
                property.offer.distance_from_the_beach ? distanceFromFormat(property.offer.distance_from_the_beach) : null,
                BeachIcon
            )
        ];
    }, [property]);

    const propertyRenderedPictograms: React.ReactNode[] = useMemo(() => {
        return propertyDetailsData.reduce<React.ReactNode[]>((acc, {label, mobileLabel, value, icon}) => {
            if (value !== null && typeof value !== "undefined") {
                acc.push(<Pictogram key={label} label={label} mobileLabel={mobileLabel} value={value as string | number} icon={icon} />);
            }
            return acc;
        }, []);
    }, [propertyDetailsData]);

    return (
        <div css={pictogramsWrapper} className={props.className}>
            {propertyRenderedPictograms}
        </div>
    );
};

const pictogramsWrapper = (theme: Theme) => css`
    display: flex;
    row-gap: 2.4rem;
    ${ph(2)};
    flex-wrap: wrap;
    align-items: flex-start;
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        row-gap: 4rem;
    }

    > div {
        ${w50};
        ${pr(2)};

        @media (min-width: ${theme.breakpoints.md}) {
            ${pr(0)};
        }
    }
`;
